import React from 'react';
import { Box, type SxProps, type Theme } from '@mui/material';
import type { SystemStyleObject } from '@mui/system';
import _ from 'lodash';
import { glassCardSx, ghostBorderSx, CINEMA_PRIMARY } from '../WatchPage/watchCinemaTheme';
import { LoadingOverlay, NoRowsOverlay } from './TableOverlay';

type MuiLikeCol = {
    field: string;
    width?: number;
    flex?: number;
    align?: 'left' | 'right' | 'center';
    headerAlign?: 'left' | 'right' | 'center';
    renderHeader: () => React.ReactNode;
    renderCell: (params: any) => React.ReactNode;
};

export interface DashboardGlassTableBodyProps {
    tableColumns: MuiLikeCol[];
    rows: any[];
    isLoading: boolean;
    headerHeight?: number;
    rowMinHeight?: number;
    textSx?: SxProps<Theme>;
    /** Custom empty state (e.g. SubmissionsTab); falls back to NoRowsOverlay */
    emptyOverlay?: React.ElementType;
    /** Dim list while search refetch runs (matches DataGrid behavior) */
    dimmed?: boolean;
    /** Navigate / open detail when the row surface is clicked */
    onRowClick?: (row: any) => void;
}

function columnFlexWeights(cols: MuiLikeCol[]): number[] {
    return cols.map(c => {
        if (typeof c.width === 'number' && c.width > 0) return c.width;
        if (typeof c.flex === 'number' && c.flex > 0) return 100 * c.flex;
        return 120;
    });
}

function justify(align?: string) {
    if (align === 'right') return 'flex-end';
    if (align === 'center') return 'center';
    return 'flex-start';
}

function buildMockParams(row: any, col: MuiLikeCol, allRows: any[]) {
    const api = {
        getAllRowIds: () => allRows.map(r => r._id),
    };
    return {
        row,
        id: row._id,
        field: col.field,
        value: _.get(row, col.field),
        api,
        colDef: col,
    };
}

export const DashboardGlassTableBody: React.FC<DashboardGlassTableBodyProps> = ({
    tableColumns,
    rows,
    isLoading,
    headerHeight = 48,
    rowMinHeight = 64,
    textSx,
    emptyOverlay: EmptyOverlayProp,
    dimmed = false,
    onRowClick,
}) => {
    const weights = columnFlexWeights(tableColumns);
    const EmptyCmp = EmptyOverlayProp || NoRowsOverlay;
    const typographyCellSx: SystemStyleObject<Theme> =
        textSx != null && typeof textSx === 'object' && !Array.isArray(textSx)
            ? (textSx as SystemStyleObject<Theme>)
            : {};

    if (!tableColumns.length) {
        return null;
    }

    if (isLoading && !rows.length) {
        return (
            <Box
                sx={{
                    minHeight: 400,
                    height: 400,
                    borderRadius: 2,
                    ...glassCardSx,
                    ...ghostBorderSx,
                }}>
                <LoadingOverlay />
            </Box>
        );
    }

    if (!isLoading && !rows.length) {
        return (
            <Box
                sx={{
                    minHeight: 280,
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    borderRadius: 2,
                    ...glassCardSx,
                    ...ghostBorderSx,
                }}>
                <EmptyCmp />
            </Box>
        );
    }

    return (
        <Box
            sx={{
                display: 'flex',
                flexDirection: 'column',
                gap: 1.25,
                width: '100%',
                opacity: dimmed ? 0.4 : 1,
                transition: 'opacity 0.2s ease-in-out',
            }}>
            <Box
                sx={{
                    display: 'flex',
                    gap: { xs: 1, sm: 1.5 },
                    alignItems: 'center',
                    px: { xs: 1.5, sm: 2 },
                    py: 1,
                    minHeight: headerHeight,
                    borderRadius: 2,
                    ...glassCardSx,
                    border: '1px solid rgba(153, 144, 124, 0.15)',
                }}>
                {tableColumns.map((col, i) => (
                    <Box
                        key={col.field || `hdr-${i}`}
                        sx={{
                            flex: `${weights[i]} 1 0px`,
                            minWidth: 0,
                            display: 'flex',
                            justifyContent: justify(col.headerAlign || col.align),
                        }}>
                        {col.renderHeader()}
                    </Box>
                ))}
            </Box>

            <Box sx={{ overflowX: 'auto', width: '100%' }}>
                <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1.25, minWidth: { xs: 640, sm: '100%' } }}>
                    {rows.map((row, ri) => (
                        <Box
                            key={row._id ?? `row-${ri}`}
                            role={onRowClick ? 'button' : undefined}
                            tabIndex={onRowClick ? 0 : undefined}
                            onClick={() => onRowClick?.(row)}
                            onKeyDown={e => {
                                if (!onRowClick) return;
                                if (e.key === 'Enter' || e.key === ' ') {
                                    e.preventDefault();
                                    onRowClick(row);
                                }
                            }}
                            sx={{
                                ...glassCardSx,
                                ...ghostBorderSx,
                                borderRadius: 3,
                                px: { xs: 1.5, sm: 2 },
                                py: 1.5,
                                display: 'flex',
                                gap: { xs: 1, sm: 1.5 },
                                alignItems: 'center',
                                minHeight: rowMinHeight,
                                transition: 'background-color 0.25s ease',
                                borderLeft: '3px solid transparent',
                                cursor: onRowClick ? 'pointer' : 'default',
                                '&:hover': {
                                    bgcolor: 'rgba(42, 42, 42, 0.55)',
                                    borderLeftColor: CINEMA_PRIMARY,
                                },
                            }}>
                            {tableColumns.map((col, i) => (
                                <Box
                                    key={col.field || `cell-${i}`}
                                    onClick={col.field === 'actions' ? e => e.stopPropagation() : undefined}
                                    sx={{
                                        flex: `${weights[i]} 1 0px`,
                                        minWidth: 0,
                                        display: 'flex',
                                        justifyContent: justify(col.align),
                                        alignItems: 'center',
                                        '& .MuiTypography-root': typographyCellSx,
                                    }}>
                                    {col.renderCell(buildMockParams(row, col, rows))}
                                </Box>
                            ))}
                        </Box>
                    ))}
                </Box>
            </Box>
        </Box>
    );
};
